import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { getUrl } from 'aws-amplify/storage';
import type { Schema } from '../amplify/data/resource';
import './ReferencesGrid.css';

interface Reference {
  id: string;
  name: string;
  website?: string;
  logoPath?: string;
  testimonial?: string;
  projectName?: string;
  projectDescription?: string;
  isActive: boolean;
}

const ReferencesGrid: React.FC = () => {
  const [references, setReferences] = useState<Reference[]>([]);
  const [referenceLogoUrls, setReferenceLogoUrls] = useState<{[key: string]: string}>({});
  const [loading, setLoading] = useState(true);

  const client = generateClient<Schema>({ authMode: 'iam' });

  // Fetch references from database
  const fetchReferences = async () => {
    try {
      console.log('🔍 Fetching references for ReferencesGrid...');
      const result = await client.models.Reference.list({
        selectionSet: ['id', 'name', 'website', 'logoPath', 'testimonial', 'projectName', 'projectDescription', 'isActive']
      });
      
      if (result.data) {
        // Filter active references and limit to 35 for display
        const activeReferences = result.data
          .filter((ref: any) => ref.isActive)
          .slice(0, 35);
        
        console.log('✅ References fetched for ReferencesGrid:', activeReferences.length);
        setReferences(activeReferences as Reference[]);
      }
    } catch (err) {
      console.error('🚨 Error fetching references for ReferencesGrid:', err);
      // Don't set error state as references are not critical for page functionality
    } finally {
      setLoading(false);
    }
  };

  // Handle reference logo click
  const handleReferenceClick = (reference: Reference) => {
    if (reference.website) {
      window.open(reference.website, '_blank', 'noopener,noreferrer');
    }
  };

  useEffect(() => {
    fetchReferences();
  }, []);

  // Resolve reference logo URLs
  useEffect(() => {
    const resolveReferenceLogos = async () => {
      const logoUrls: {[key: string]: string} = {};
      
      for (const reference of references) {
        if (reference.logoPath) {
          try {
            if (reference.logoPath.startsWith('http') || reference.logoPath.startsWith('/')) {
              logoUrls[reference.id] = reference.logoPath;
            } else {
              // Use S3 URLs with local fallback
              try {
                const publicPath = reference.logoPath.startsWith('public/') ? reference.logoPath : `public/${reference.logoPath}`;
                const { url } = await getUrl({ path: publicPath });
                logoUrls[reference.id] = url.toString();
              } catch (s3Error) {
                console.warn(`S3 access failed for reference ${reference.name}, using local fallback:`, s3Error);
                // Fallback to local assets if S3 fails
                const fallbackPath = reference.logoPath.replace('public/', '/assets/images/');
                logoUrls[reference.id] = fallbackPath;
              }
            }
          } catch (e) {
            console.warn(`Failed to resolve logo for reference ${reference.name}:`, e);
            logoUrls[reference.id] = '';
          }
        }
      }
      
      setReferenceLogoUrls(logoUrls);
    };

    if (references.length > 0) {
      resolveReferenceLogos();
    }
  }, [references]);



  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    target.src = `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiB2aWV3Qm94PSIwIDAgMTIwIDgwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjRjVGNUY1IiBzdHJva2U9IiNFMEUwRTAiLz4KPHR5cGU+TG9nbzwvdHlwZT4KPC9zdmc+`;
    target.alt = 'Participant Logo Placeholder';
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.preventDefault();
    return false;
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
  };

  return (
    <section className="references-grid-section" data-section="ki-kurse_references-grid" data-page="ki-kurse">
      <div className="container references-grid-container">
        <div className="references-grid-header">
          <h2>Marktführer haben <span className="highlight-text">Vertrauen</span> in unsere KI-Kurse</h2>
        </div>
        <div className="references-grid">
          {loading ? (
            // Loading placeholder
            Array.from({ length: 12 }, (_, index) => (
              <div key={`loading-${index}`} className="reference-grid-item loading">
                <div className="loading-placeholder"></div>
              </div>
            ))
          ) : references.length === 0 ? (
            // Show message when no references are available
            <div style={{ 
              gridColumn: '1 / -1', 
              textAlign: 'center', 
              padding: '2rem',
              color: '#6b7280',
              fontSize: '14px'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '1rem' }}>🏢</div>
              <p>Referenzen werden geladen...</p>
            </div>
          ) : (
            references.map((reference) => {
              const logoUrl = referenceLogoUrls[reference.id];
              if (!logoUrl) return null;
              
              return (
                <div 
                  key={reference.id} 
                  className={`reference-grid-item ${reference.website ? 'clickable' : ''}`}
                  onClick={() => handleReferenceClick(reference)}
                  title={reference.website ? `Besuchen Sie ${reference.name}` : reference.name}
                >
                  <img
                    src={logoUrl}
                    alt={`${reference.name} Logo`}
                    className="reference-grid-image"
                    onError={handleImageError}
                    onDragStart={handleDragStart}
                    onContextMenu={handleContextMenu}
                    draggable={false}
                  />
                </div>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
};

export default ReferencesGrid;
